import 'ol-geocoder/dist/ol-geocoder.min.css'
import 'ol-popup/src/ol-popup.css'

import React, { useRef, useEffect } from 'react'
import Draw from 'ol/interaction/Draw'
import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { Style } from 'ol/style'
import {
  FullScreen,
  Attribution,
  defaults as defaultControls,
} from 'ol/control'
import { ScaleLine } from 'ol/control'
import Geocoder from 'ol-geocoder'
import Popup from 'ol-popup'
import { useDispatch, useSelector } from 'react-redux'
import { getArea, getLength } from 'ol/sphere'
import * as ol from 'ol'
import { drawFeaturesStyles } from './OlMapStyles'

// Import Redux action(s)
import { setZoom, setOlMap, setArea } from '../../redux/actions/map.actions'

// Import Open Layers default styles
import 'ol/ol.css'

// Import Open Layers styles overrides
import './Map.scss'

const attribution = new Attribution({
  collapsible: false,
});

// Define layers for drawing functionality
const source = new VectorSource({ wrapX: false })

const vectorLayer = new VectorLayer({
  source: source,
  style: drawFeaturesStyles,
  zIndex: 2,
})

const draw = new Draw({
  source: source,
  style: drawFeaturesStyles,
  type: 'Polygon',
  stopClick: true,
})

const Map = ({ children }) => {
  const mapRef = useRef()
  const dispatch = useDispatch()
  const { olMap, zoom, center, isAreaDrawingEnabled, clearAreaDrawing } =
    useSelector((state) => state.mapStates)

  useEffect(() => {
    let options = {
      view: new ol.View({
        zoom,
        center,
        projection: 'EPSG:4326',
        maxZoom: 18,
        minZoom: 9,
      }),
      layers: [],
      controls: defaultControls({ attribution: false }).extend([
        new FullScreen(),
        attribution,
        new ScaleLine({
          units: 'metric',
        }),
      ]),
      overlays: [],
    }
    let mapObject = new ol.Map(options)
    var geocoder = new Geocoder('nominatim', {
      provider: 'photon',
      lang: 'en',
      placeholder: 'Search for ...',
      limit: 5,
      featureStyle: new Style({
        zIndex: 1000,
      }),
      debug: false,
      autoComplete: true,
      keepOpen: true,
    })

    var popup = new Popup()
    mapObject.addOverlay(popup)

    geocoder.on('addresschosen', function (evt) {
      window.setTimeout(function () {
        popup.show(evt.coordinate, evt.address.formatted)
      }, 100)
    })

    mapObject.addControl(geocoder)
    mapObject.setTarget(mapRef.current)
    dispatch(setOlMap(mapObject))
    return () => mapObject.setTarget(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!olMap) return
    // If drawing disabled or user clear features, then remove existing vector features,
    // drawing interaction and vector layer.
    if (clearAreaDrawing || !isAreaDrawingEnabled) {
      source.clear()
      olMap.removeInteraction(draw)
      olMap.removeLayer(vectorLayer)
    } else {
      // If drawing enabled, then lets add a vector layer and drawing interaction.
      if (isAreaDrawingEnabled) {
        olMap.addLayer(vectorLayer)
        olMap.addInteraction(draw)
      }
    }
  }, [dispatch, olMap, isAreaDrawingEnabled, clearAreaDrawing])

  useEffect(() => {
    // If the user start new feature drawing then clear the last one.
    draw.on('drawstart', function (e) {
      if (source) {
        source.clear()
      }
    })

    // After user finish drawing then collect the main properties of the
    // new feature and save them to Redux store.
    draw.on('drawend', function (e) {
      const polygon = e.feature.values_.geometry
      const projectedArea = getArea(polygon, { projection: 'EPSG:4326' }) // Area in m2
      const projectedPerimeter = getLength(polygon, {
        projection: 'EPSG:4326',
      }) // Perimeter in m
      const area = Math.round((projectedArea / 1000000) * 100) / 100 // Area in km2
      const perimeter = (projectedPerimeter / 1000).toFixed(3) // Perimeter in km
      const coordinates = polygon.getCoordinates()

      // Object which will be saved in store.
      const arePayload = {
        polygonPerimeter: perimeter,
        polygonArea: area,
        polygonCoordinates: coordinates,
      }

      if (arePayload.polygonArea > 3) {
        alert('The maximum area is 3 km2')
      } else {
        dispatch(setArea(arePayload))
      }
    })
  }, [dispatch])

  useEffect(() => {
    if (!olMap) return

    // In order to reach the current zoom level from different component,
    // then save it to store after every zoom end.
    olMap.on('moveend', function (_) {
      let currentZoom = olMap.getView().getZoom()
      if (currentZoom !== zoom) {
        dispatch(setZoom(currentZoom))
      }
    })
    // eslint-disable-next-line
  }, [dispatch, olMap])

  return (
    <div ref={mapRef} id="ol-map">
      {children}
    </div>
  )
}
export default Map
